/**
 * Wishlist — localStorage-backed with cookie sync for server access.
 * Stores product handles as a JSON array.
 * Syncs to a cookie so Liquid can read the wishlist count.
 */
const STORAGE_KEY = "la-femme-wishlist";
const COOKIE_NAME = "wishlist_count";

const Wishlist = {
  getItems() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  },

  save(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    // Sync count to cookie for potential server-side use
    document.cookie = `${COOKIE_NAME}=${items.length};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
    document.dispatchEvent(
      new CustomEvent("wishlist:updated", { detail: { count: items.length } }),
    );
  },

  has(handle) {
    return this.getItems().includes(handle);
  },

  add(handle) {
    const items = this.getItems();
    if (!items.includes(handle)) {
      items.push(handle);
      this.save(items);
    }
  },

  remove(handle) {
    const items = this.getItems().filter((h) => h !== handle);
    this.save(items);
  },

  toggle(handle) {
    if (this.has(handle)) {
      this.remove(handle);
      return false;
    } else {
      this.add(handle);
      return true;
    }
  },

  count() {
    return this.getItems().length;
  },
};

// ── Toggle buttons (PDP + any page) ──
function initWishlistToggle() {
  document.querySelectorAll("[data-wishlist-toggle]").forEach((btn) => {
    const handle =
      btn.closest("[data-product-handle]")?.dataset.productHandle ||
      document.querySelector("[data-product-handle]")?.dataset.productHandle ||
      window.location.pathname.split("/products/")[1];

    if (!handle) return;

    // Set initial state
    updateToggleButton(btn, Wishlist.has(handle));

    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const added = Wishlist.toggle(handle);
      updateToggleButton(btn, added);
    });
  });
}

function updateToggleButton(btn, isActive) {
  const svg = btn.querySelector("svg");
  if (svg) {
    const path = svg.querySelector("path");
    if (path) {
      if (isActive) {
        path.setAttribute("fill", "currentColor");
      } else {
        path.setAttribute("fill", "none");
      }
    }
  }
  btn.setAttribute("aria-pressed", isActive);
  btn.setAttribute(
    "aria-label",
    isActive ? "Remove from wishlist" : "Add to wishlist",
  );
}

// ── Wishlist count in header ──
function updateWishlistCount() {
  const count = Wishlist.count();
  document.querySelectorAll("[data-wishlist-count]").forEach((el) => {
    el.textContent = count;
    el.classList.toggle("hidden", count === 0);
  });
}

// ── Wishlist page ──
function initWishlistPage() {
  const container = document.querySelector("[data-wishlist-grid]");
  if (!container) return;

  const emptyState = document.querySelector("[data-wishlist-empty]");
  const items = Wishlist.getItems();

  if (items.length === 0) {
    container.style.display = "none";
    if (emptyState) emptyState.style.display = "";
    return;
  }

  if (emptyState) emptyState.style.display = "none";

  // Fetch each product card via section rendering API
  const sectionId = container.dataset.sectionId;
  const fetches = items.map((handle) =>
    fetch(`/products/${handle}?section_id=${sectionId}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Product not found: ${handle}`);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const card = doc.querySelector("[data-wishlist-card]");
        return card ? card.innerHTML : null;
      })
      .catch(() => {
        // Product might have been removed, clean up
        Wishlist.remove(handle);
        return null;
      }),
  );

  Promise.all(fetches).then((cards) => {
    const validCards = cards.filter(Boolean);
    if (validCards.length === 0) {
      container.style.display = "none";
      if (emptyState) emptyState.style.display = "";
      return;
    }
    container.innerHTML = validCards.join("");
    container.style.display = "";
  });
}

// ── Remove from wishlist page ──
document.addEventListener("click", (e) => {
  const btn = e.target.closest("[data-wishlist-remove]");
  if (!btn) return;

  e.preventDefault();
  const handle = btn.dataset.wishlistRemove;
  if (handle) {
    Wishlist.remove(handle);
    const card = btn.closest("[data-product-card]");
    if (card) {
      card.style.transition = "opacity 0.3s";
      card.style.opacity = "0";
      setTimeout(() => {
        card.remove();
        // Check if empty
        const container = document.querySelector("[data-wishlist-grid]");
        const emptyState = document.querySelector("[data-wishlist-empty]");
        if (container && container.children.length === 0) {
          container.style.display = "none";
          if (emptyState) emptyState.style.display = "";
        }
      }, 300);
    }
    updateWishlistCount();
  }
});

// ── Init ──
document.addEventListener("DOMContentLoaded", () => {
  initWishlistToggle();
  updateWishlistCount();
  initWishlistPage();
});

document.addEventListener("wishlist:updated", () => {
  updateWishlistCount();
});

window.Wishlist = Wishlist;
